import {
  Avatar,
  Button,
  Divider,
  Group,
  Modal,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useEffect } from 'react';
import { useUpdateUser } from '@/models/adminUsers';
import type { User } from '@/types/admin';

interface EditUserModalProps {
  opened: boolean;
  onClose: () => void;
  user: User | null;
}

export function EditUserModal({ opened, onClose, user }: EditUserModalProps) {
  const updateMutation = useUpdateUser();

  const form = useForm({
    initialValues: {
      whatsapp: '',
      telegram: '',
      zadarmaExtension: '',
      title: '',
      bio: '',
      photoUrl: '',
    },
    validate: {
      photoUrl: (v) =>
        !v.trim() || /^(https:\/\/|\/)/.test(v.trim())
          ? null
          : 'Use an https:// link or a site path such as /team/name.webp',
    },
  });

  // Reset form when user changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: form identity is not stable across renders
  useEffect(() => {
    if (user && opened) {
      form.setValues({
        whatsapp: user.whatsapp || '',
        telegram: user.telegram || '',
        zadarmaExtension: user.zadarmaExtension || '',
        title: user.title || '',
        bio: user.bio || '',
        photoUrl: user.photoUrl || '',
      });
    } else if (!opened) {
      form.reset();
    }
  }, [user, opened]);

  const handleSubmit = form.onSubmit((values) => {
    if (!user) return;

    updateMutation.mutate(
      {
        userId: user.id,
        whatsapp: values.whatsapp.trim() || undefined,
        telegram: values.telegram.trim() || undefined,
        zadarmaExtension: values.zadarmaExtension.trim(),
        title: values.title.trim(),
        bio: values.bio.trim(),
        photoUrl: values.photoUrl.trim(),
      },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  });

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Modal opened={opened} onClose={handleClose} title="Edit User" size="md">
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <TextInput label="Email" value={user?.email || ''} disabled />

          <TextInput
            label="Name"
            placeholder="User name"
            value={user?.info.name || ''}
            disabled
          />

          <TextInput
            label="WhatsApp"
            placeholder="WhatsApp number"
            {...form.getInputProps('whatsapp')}
          />

          <TextInput
            label="Telegram"
            placeholder="Telegram username"
            {...form.getInputProps('telegram')}
          />

          <TextInput
            label="Zadarma extension"
            placeholder="e.g. 101"
            {...form.getInputProps('zadarmaExtension')}
          />

          <Divider
            label="Point of contact card on customer offers"
            labelPosition="left"
          />
          <Text fz="xs" c="dimmed">
            Shown to customers on every offer this user sends. Empty fields are
            left out of the card.
          </Text>
          <Group align="flex-end" wrap="nowrap">
            <Avatar
              src={form.values.photoUrl.trim() || user?.info.picture || null}
              size={56}
              radius="md"
            />
            <TextInput
              style={{ flex: 1 }}
              label="Photo"
              placeholder="/team/name.webp or https://..."
              {...form.getInputProps('photoUrl')}
            />
          </Group>
          <TextInput
            label="Job title"
            placeholder="e.g. Co-Founder and Managing Partner"
            {...form.getInputProps('title')}
          />
          <Textarea
            label="Short bio"
            autosize
            minRows={3}
            maxRows={8}
            maxLength={1200}
            {...form.getInputProps('bio')}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" loading={updateMutation.isPending}>
              Save Changes
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
